import { useState, type FormEvent } from "react";
import {
  completeNewPassword,
  NewPasswordRequired,
  signIn,
  type SignedIn,
} from "../lib/auth";
import { authProblem } from "../lib/authMessages";
import { PasswordReset } from "./PasswordReset";
import type { CognitoUser } from "amazon-cognito-identity-js";

export function SignIn({ onSignedIn }: { onSignedIn: (s: SignedIn) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  // Set when Cognito says the temporary password has to be replaced. That is a
  // step, not a failure, and it gets its own form rather than an error message.
  const [mustChange, setMustChange] = useState<CognitoUser | null>(null);
  const [resetting, setResetting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      if (mustChange) {
        onSignedIn(await completeNewPassword(mustChange, email, newPassword));
      } else {
        onSignedIn(await signIn(email, password));
      }
    } catch (err) {
      if (err instanceof NewPasswordRequired) {
        setMustChange(err.user);
        setProblem(null);
      } else if ((err as { name?: string })?.name === "PasswordResetRequiredException") {
        // Cognito has already decided this account needs a new password, which
        // is the state an administrator reset leaves it in. Until 2026-09-15
        // this printed a sentence naming that state and offered no way out of
        // it, so it went straight to the screen that can finish it.
        setResetting(true);
        setProblem(null);
      } else {
        setProblem(authProblem(err, "signin"));
      }
    } finally {
      setBusy(false);
    }
  }

  if (resetting) {
    return (
      <PasswordReset
        initialEmail={email}
        onSignedIn={onSignedIn}
        onCancel={() => { setResetting(false); setProblem(null); }}
      />
    );
  }

  return (
    <main style={{ maxWidth: "22rem" }}>
      <h1>Sign in to EVIE Sales</h1>
      <p className="muted">Buzze accounts only.</p>
      <form onSubmit={submit}>
        <p>
          <label htmlFor="email">Email</label><br />
          <input
            id="email" type="email" autoComplete="username" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            disabled={mustChange !== null} style={{ width: "100%" }}
          />
        </p>
        {mustChange ? (
          <>
            <p>
              Your password was a temporary one. Choose a new one to finish
              signing in.
            </p>
            <p>
              <label htmlFor="newpw">New password</label><br />
              <input
                id="newpw" type="password" autoComplete="new-password" required
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                style={{ width: "100%" }}
              />
            </p>
          </>
        ) : (
          <p>
            <label htmlFor="pw">Password</label><br />
            <input
              id="pw" type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              style={{ width: "100%" }}
            />
          </p>
        )}
        <button type="submit" disabled={busy}>
          {busy ? "Signing in" : mustChange ? "Set password and sign in" : "Sign in"}
        </button>
      </form>
      {problem ? <p className="refusal" role="alert">{problem}</p> : null}
      {mustChange ? null : (
        <p>
          <button type="button" className="link" onClick={() => setResetting(true)}>
            Reset your password
          </button>
        </p>
      )}
    </main>
  );
}
